import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Search,
  Phone,
  Mail,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Users,
  ShieldCheck,
  CreditCard,
} from "lucide-react";
import { format } from "date-fns";

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

type ChildInfo = {
  id: string;
  firstName: string;
  lastName: string;
  grade: number;
  school: string | null;
  subscriptionStatus: string | null;
  subscriptionEndDate: string | null;
  level: number;
  xp: number;
  accuracy: number | null;
  lastActive: string | null;
  createdAt: string;
};

type ParentRecord = {
  parentEmail: string | null;
  parentPhone: string | null;
  parentFirstName: string | null;
  parentLastName: string | null;
  whatsappVerified: boolean;
  children: ChildInfo[];
  childCount: number;
  hasActiveSubscription: boolean;
};

export default function AdminParents() {
  // /admin/parents?search=<email|phone> deep-links from the Subscriptions console; matches are expanded on first load.
  const initialSearch = (() => { try { return new URLSearchParams(window.location.search).get("search") || ""; } catch { return ""; } })();
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [whatsappFilter, setWhatsappFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [autoExpanded, setAutoExpanded] = useState(!initialSearch);

  const debouncedSearch = useDebounce(searchTerm, 300);

  const buildUrl = () => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (whatsappFilter !== "all") params.set("whatsapp", whatsappFilter);
    if (subscriptionFilter !== "all") params.set("subscription", subscriptionFilter);
    const qs = params.toString();
    return `/api/admin/parents${qs ? `?${qs}` : ""}`;
  };

  const { data: parents, isLoading } = useQuery<ParentRecord[]>({
    queryKey: [buildUrl()],
  });

  useEffect(() => {
    if (autoExpanded || !parents) return;
    setExpandedRows(new Set(parents.slice(0, 5).map(p => p.parentEmail || p.parentPhone || "")));
    setAutoExpanded(true);
  }, [parents, autoExpanded]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const totalParents = parents?.length || 0;
  const whatsappVerifiedCount = parents?.filter(p => p.whatsappVerified).length || 0;
  const activeSubCount = parents?.filter(p => p.hasActiveSubscription).length || 0;

  const getParentKey = (p: ParentRecord) => p.parentEmail || p.parentPhone || "";

  const getSubscriptionBadge = (status: string | null) => {
    const validStatuses = ["active", "trial", "trialing"];
    if (status && validStatuses.includes(status)) {
      return <Badge variant="default">Active</Badge>;
    }
    return <Badge variant="secondary">Inactive</Badge>;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Parent Directory</h1>
          <p className="text-muted-foreground mt-1">View parent/guardian information and their linked children</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Parent Directory</h1>
        <p className="text-muted-foreground mt-1">View parent/guardian information and their linked children</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Parents</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalParents.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">WhatsApp Verified</CardTitle>
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{whatsappVerifiedCount.toLocaleString()}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">With Active Subscriptions</CardTitle>
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeSubCount.toLocaleString()}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search parents or children..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={whatsappFilter} onValueChange={setWhatsappFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="WhatsApp" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All WhatsApp</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="unverified">Not Verified</SelectItem>
                </SelectContent>
              </Select>
              <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Subscription" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subscriptions</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!parents || parents.length === 0 ? (
            <div className="text-center py-12">
              <Users className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <p className="mt-4 text-muted-foreground">No parents found</p>
              {(debouncedSearch || whatsappFilter !== "all" || subscriptionFilter !== "all") && (
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Parent Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Children</TableHead>
                    <TableHead>Subscription</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parents.map((parent) => {
                    const key = getParentKey(parent);
                    const isExpanded = expandedRows.has(key);
                    const parentName = parent.parentFirstName || parent.parentLastName
                      ? `${parent.parentFirstName || ""} ${parent.parentLastName || ""}`.trim()
                      : null;
                    const childrenText = parent.children
                      .map(c => `${c.firstName} (Gr. ${c.grade})`)
                      .join(", ");

                    return (
                      <>
                        <TableRow key={key}>
                          <TableCell>
                            {parentName ? (
                              <span className="font-medium">{parentName}</span>
                            ) : (
                              <span className="text-muted-foreground">Not provided</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              {parent.parentEmail && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="truncate max-w-48">{parent.parentEmail}</span>
                                </div>
                              )}
                              {parent.parentPhone && (
                                <div className="flex items-center gap-1 text-sm">
                                  <Phone className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span>{parent.parentPhone}</span>
                                  {parent.whatsappVerified && (
                                    <MessageSquare className="h-3 w-3 text-green-600 flex-shrink-0" />
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <Badge variant="secondary">{parent.childCount}</Badge>
                              <div className="text-sm text-muted-foreground max-w-60 truncate" title={childrenText}>
                                {childrenText}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {parent.hasActiveSubscription ? (
                              <Badge variant="default">Active</Badge>
                            ) : (
                              <Badge variant="secondary">Inactive</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleRow(key)}
                            >
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 mr-1" />
                              ) : (
                                <ChevronRight className="h-4 w-4 mr-1" />
                              )}
                              View Details
                            </Button>
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${key}-details`}>
                            <TableCell colSpan={5} className="p-4 bg-muted/30">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {parent.children.map((child) => (
                                  <Card key={child.id}>
                                    <CardContent className="p-4">
                                      <div className="flex items-start justify-between gap-2 flex-wrap">
                                        <div>
                                          <p className="font-medium">{child.firstName} {child.lastName}</p>
                                          <p className="text-sm text-muted-foreground">
                                            Grade {child.grade}{child.school ? ` - ${child.school}` : ""}
                                          </p>
                                        </div>
                                        {getSubscriptionBadge(child.subscriptionStatus)}
                                      </div>
                                      <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
                                        <div>
                                          <span className="text-muted-foreground">Level:</span>{" "}
                                          <span className="font-medium">{child.level}</span>
                                        </div>
                                        <div>
                                          <span className="text-muted-foreground">XP:</span>{" "}
                                          <span className="font-medium">{child.xp.toLocaleString()}</span>
                                        </div>
                                        {child.accuracy !== null && (
                                          <div>
                                            <span className="text-muted-foreground">Accuracy:</span>{" "}
                                            <span className="font-medium">{child.accuracy}%</span>
                                          </div>
                                        )}
                                        <div>
                                          <span className="text-muted-foreground">Last Active:</span>{" "}
                                          <span className="font-medium">
                                            {child.lastActive
                                              ? format(new Date(child.lastActive), "MMM d, yyyy")
                                              : "N/A"}
                                          </span>
                                        </div>
                                        {child.subscriptionEndDate && (
                                          <div>
                                            <span className="text-muted-foreground">Sub. Ends:</span>{" "}
                                            <span className="font-medium">
                                              {format(new Date(child.subscriptionEndDate), "MMM d, yyyy")}
                                            </span>
                                          </div>
                                        )}
                                        <div>
                                          <span className="text-muted-foreground">Joined:</span>{" "}
                                          <span className="font-medium">
                                            {format(new Date(child.createdAt), "MMM d, yyyy")}
                                          </span>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
